/**
 * @module AuditContext
 * @description Contexto global para la gestión completa del módulo de auditoría ISO 27001/27002.
 *
 * Cubre el ciclo de vida completo de una auditoría de seguridad:
 *
 *   1. **Planes de auditoría** (`audit_plans`) — programación, alcance, equipo auditor
 *   2. **Hallazgos** (`audit_findings`) — no conformidades, observaciones, oportunidades
 *   3. **Acciones correctivas** (`corrective_actions`) — planes de remediación vinculados a hallazgos
 *   4. **Métricas** (`get_audit_metrics` RPC) — KPIs calculados en PostgreSQL para rendimiento
 *   5. **Reportes guardados** (`audit_reports`) — historial con archivos en Supabase Storage
 *   6. **Plantillas de reporte** (`report_templates`) — branding y secciones configurables
 *   7. **Programaciones de reporte** (`report_schedules`) — envío automático periódico
 *
 * ## Convenciones de nomenclatura
 * Todas las funciones convierten entre camelCase (aplicación) y snake_case (PostgreSQL).
 * El mapeo es explícito campo por campo para garantizar type-safety sin depender de
 * conversiones automáticas que puedan fallar silenciosamente.
 *
 * ## Carga diferida (no al montar)
 * Los datos se cargan en `refreshData()` que se dispara cuando:
 *   - `user.organization_id` está disponible Y
 *   - `dbUserReady` es `true` (garantiza que el UUID de PostgreSQL es válido)
 * El estado inicial es `isLoading: false` (no spinner mientras no haya sesión).
 *
 * ## Flujo de remediación de hallazgos
 * ```
 * AuditFinding (open) → createCorrectiveAction() → Finding (in_remediation)
 *                     → completeCorrectiveAction() → Action (completed)
 *                     → verifyCorrectiveAction()   → Action (verified)
 *                     → verifyFinding()             → Finding (verified)
 * ```
 *
 * @see src/components/Audit/ — componentes de UI del módulo
 * @see supabase/migrations/ — esquema de tablas de auditoría
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';
import type {
  AuditPlan,
  AuditFinding,
  CorrectiveAction,
  AuditMetrics,
  AuditReport,
  ReportTemplate,
  ReportSchedule,
  User
} from '../types/iso';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Contrato del contexto de auditoría expuesto a los componentes consumidores.
 * Organizado en siete grupos funcionales para mayor claridad en IntelliSense.
 *
 * Todas las funciones CRUD siguen el patrón:
 *   - `load*()` — carga desde BD al estado React
 *   - `create*()` — INSERT en BD + reload
 *   - `update*()` — UPDATE en BD + reload
 *   - `delete*()` — DELETE en BD + reload
 *   - Funciones especiales de transición de estado (resolve, verify, complete)
 */
interface AuditContextType {
  // ── Estados principales ──────────────────────────────────────────────────
  auditPlans: AuditPlan[];
  findings: AuditFinding[];
  correctiveActions: CorrectiveAction[];
  /** Métricas calculadas por la función RPC `get_audit_metrics` — null mientras carga */
  auditMetrics: AuditMetrics | null;

  // ── Estados Fase 3.5 — Reportes ──────────────────────────────────────────
  /** Historial de reportes generados con sus URLs en Supabase Storage */
  savedReports: AuditReport[];
  /** Plantillas de marca y secciones para generación de reportes */
  reportTemplates: ReportTemplate[];
  /** Programaciones de envío automático periódico de reportes */
  reportSchedules: ReportSchedule[];

  // ── Estados de UI ────────────────────────────────────────────────────────
  isLoading: boolean;
  error: string | null;

  // ── Funciones de utilidad ────────────────────────────────────────────────
  clearError: () => void;
  /** Recarga todos los módulos en paralelo + métricas secuencialmente */
  refreshData: () => Promise<void>;

  // ── Planes de auditoría ──────────────────────────────────────────────────
  loadAuditPlans: () => Promise<void>;
  createAuditPlan: (plan: Omit<AuditPlan, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<void>;
  updateAuditPlan: (planId: string, updates: Partial<AuditPlan>) => Promise<void>;
  deleteAuditPlan: (planId: string) => Promise<void>;

  // ── Hallazgos ────────────────────────────────────────────────────────────
  loadFindings: () => Promise<void>;
  createFinding: (finding: Omit<AuditFinding, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<void>;
  updateFinding: (findingId: string, updates: Partial<AuditFinding>) => Promise<void>;
  deleteFinding: (findingId: string) => Promise<void>;
  /** Marca el hallazgo como resuelto con notas de resolución (open → resolved) */
  resolveFinding: (findingId: string, resolutionNotes: string) => Promise<void>;
  /** Verifica la resolución del hallazgo (resolved → verified), registra al verificador */
  verifyFinding: (findingId: string) => Promise<void>;

  // ── Acciones correctivas ─────────────────────────────────────────────────
  loadCorrectiveActions: () => Promise<void>;
  createCorrectiveAction: (action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<void>;
  updateCorrectiveAction: (actionId: string, updates: Partial<CorrectiveAction>) => Promise<void>;
  deleteCorrectiveAction: (actionId: string) => Promise<void>;
  /** Marca acción como completada (→ completed) con evidencia de verificación */
  completeCorrectiveAction: (actionId: string, verificationEvidence: string) => Promise<void>;
  /** Verifica la efectividad de la acción completada (→ verified) */
  verifyCorrectiveAction: (actionId: string, effectivenessReview: string) => Promise<void>;

  // ── Métricas ─────────────────────────────────────────────────────────────
  loadAuditMetrics: () => Promise<void>;

  // ── Fase 3.5 — Historial de reportes ─────────────────────────────────────
  loadSavedReports: () => Promise<void>;
  /**
   * Sube el blob a Supabase Storage y guarda los metadatos en `audit_reports`.
   * @returns El objeto `AuditReport` creado, o `null` si falla
   */
  saveReport: (blob: Blob, filename: string, meta: {
    name: string;
    reportType: AuditReport['reportType'];
    format: AuditReport['format'];
    auditPlanId?: string;
    templateId?: string;
    filters?: AuditReport['filters'];
    includeSections: AuditReport['includeSections'];
    findingsCount?: number;
    actionsCount?: number;
  }) => Promise<AuditReport | null>;
  deleteReport: (reportId: string) => Promise<void>;
  /** Genera una URL firmada fresca y descarga el archivo desde Supabase Storage */
  downloadReport: (report: AuditReport) => Promise<void>;

  // ── Fase 3.5 — Plantillas ────────────────────────────────────────────────
  loadReportTemplates: () => Promise<void>;
  createReportTemplate: (template: Omit<ReportTemplate, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<ReportTemplate | null>;
  updateReportTemplate: (templateId: string, updates: Partial<ReportTemplate>) => Promise<void>;
  deleteReportTemplate: (templateId: string) => Promise<void>;
  /** Sube logo al Storage y actualiza `logoUrl` y `logoPath` en la plantilla */
  uploadTemplateLogo: (templateId: string, file: File) => Promise<string | null>;

  // ── Fase 3.5 — Programación ──────────────────────────────────────────────
  loadReportSchedules: () => Promise<void>;
  createReportSchedule: (schedule: Omit<ReportSchedule, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<void>;
  updateReportSchedule: (scheduleId: string, updates: Partial<ReportSchedule>) => Promise<void>;
  deleteReportSchedule: (scheduleId: string) => Promise<void>;
  /** Shortcut para activar/desactivar una programación sin actualizar otros campos */
  toggleScheduleActive: (scheduleId: string, isActive: boolean) => Promise<void>;
}

// =====================================================
// CONTEXTO
// =====================================================

/** Contexto React interno — consumir mediante `useAudit()`. */
const AuditContext = createContext<AuditContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

/**
 * Proveedor del contexto de auditoría. Debe estar dentro de `AuthProvider`.
 *
 * @param props.children - Árbol de componentes React que consumirán el contexto
 */
export function AuditProvider({ children }: { children: React.ReactNode }) {
  const { user, dbUserReady } = useAuth();

  // ── Estados principales ──────────────────────────────────────────────────
  const [auditPlans, setAuditPlans] = useState<AuditPlan[]>([]);
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [correctiveActions, setCorrectiveActions] = useState<CorrectiveAction[]>([]);
  const [auditMetrics, setAuditMetrics] = useState<AuditMetrics | null>(null);

  // ── Estados Fase 3.5 — Reportes ──────────────────────────────────────────
  const [savedReports, setSavedReports] = useState<AuditReport[]>([]);
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [reportSchedules, setReportSchedules] = useState<ReportSchedule[]>([]);

  // ── Estados de UI ────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  /** Limpia el error actual — llamado desde UI tras mostrar el mensaje al usuario. */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Centraliza el manejo de errores del módulo de auditoría.
   * Extrae el mensaje del error si es instancia de `Error`, o usa texto genérico.
   *
   * @param err     - Error capturado (tipo unknown del catch)
   * @param context - Nombre de la operación para el log (ej: 'cargar planes de auditoría')
   */
  const handleError = useCallback((err: unknown, context: string) => {
    logger.error(`[AUDIT ERROR] ${context}:`, err);
    const errorMessage = err instanceof Error ? err.message : `Error en ${context}`;
    setError(errorMessage);
  }, []);

  // =====================================================
  // FUNCIONES DE PLANES DE AUDITORÍA
  // =====================================================

  /**
   * Carga todos los planes de auditoría de la organización, con JOIN al auditor líder.
   * El join `users!audit_plans_lead_auditor_id_fkey` usa la clave foránea explícita
   * porque Supabase necesita desambiguar si hay varias FKs hacia la tabla `users`.
   *
   * Transforma el resultado de BD (snake_case) al formato camelCase de la aplicación,
   * construyendo el objeto `leadAuditor` anidado con campos mínimos del tipo `User`.
   */
  const loadAuditPlans = useCallback(async () => {
    if (!user?.organization_id) return;

    try {
      logger.log('[AUDIT] 🔄 Cargando planes de auditoría...');

      const { data, error: queryError } = await supabase
        .from('audit_plans')
        .select(`
          *,
          lead_auditor:users!audit_plans_lead_auditor_id_fkey (
            id, name, email, role, unit
          )
        `)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      const formattedPlans: AuditPlan[] = (data || []).map((plan: Record<string, unknown>) => ({
        id: plan.id,
        organizationId: plan.organization_id,
        name: plan.name,
        auditType: plan.audit_type,
        scope: plan.scope,
        objectives: plan.objectives,
        methodology: plan.methodology,
        startDate: plan.start_date,
        endDate: plan.end_date,
        leadAuditorId: plan.lead_auditor_id,
        leadAuditor: plan.lead_auditor ? {
          id: plan.lead_auditor.id,
          name: plan.lead_auditor.name,
          email: plan.lead_auditor.email,
          role: plan.lead_auditor.role,
          unit: plan.lead_auditor.unit,
          createdAt: '',    // Campo requerido por el tipo User, no disponible en el join selectivo
          isActive: true
        } : undefined,
        auditTeam: plan.audit_team || [],
        status: plan.status,
        metadata: plan.metadata,
        createdAt: plan.created_at,
        updatedAt: plan.updated_at
      }));

      setAuditPlans(formattedPlans);
      logger.log(`[AUDIT] ✅ Cargados ${formattedPlans.length} planes de auditoría`);
    } catch (err) {
      handleError(err, 'cargar planes de auditoría');
    }
  }, [user, handleError]);

  /**
   * Crea un nuevo plan de auditoría en la organización del usuario autenticado.
   * Tras crear, recarga todos los planes para garantizar consistencia con la BD.
   *
   * @param plan - Datos del plan sin los campos generados por PostgreSQL (id, timestamps, org)
   * @throws Error si el INSERT falla (relanzado para manejo en el componente)
   */
  const createAuditPlan = useCallback(async (plan: Omit<AuditPlan, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[AUDIT] 📝 Creando plan de auditoría...', plan);

      const { data, error: insertError } = await supabase
        .from('audit_plans')
        .insert({
          organization_id: user.organization_id,
          name: plan.name,
          audit_type: plan.auditType,
          scope: plan.scope,
          objectives: plan.objectives,
          methodology: plan.methodology,
          start_date: plan.startDate,
          end_date: plan.endDate,
          lead_auditor_id: plan.leadAuditorId,
          audit_team: plan.auditTeam,
          status: plan.status,
          metadata: plan.metadata || {}
        })
        .select()
        .single();

      if (insertError) throw insertError;

      logger.log('[AUDIT] ✅ Plan de auditoría creado:', data);
      // Recargar para incluir el JOIN con el auditor líder
      await loadAuditPlans();
    } catch (err) {
      handleError(err, 'crear plan de auditoría');
      throw err;
    }
  }, [user, loadAuditPlans, handleError]);

  /**
   * Actualiza un plan de auditoría existente.
   * Solo incluye en el UPDATE los campos que están presentes en `updates`
   * (verificación explícita `!== undefined`) para evitar sobreescribir campos
   * con undefined si el llamador solo quiere actualizar un subconjunto.
   *
   * @param planId  - UUID del plan a actualizar
   * @param updates - Campos del plan en camelCase (convertidos a snake_case para la BD)
   */
  const updateAuditPlan = useCallback(async (planId: string, updates: Partial<AuditPlan>) => {
    try {
      logger.log('[AUDIT] 🔄 Actualizando plan de auditoría:', planId, updates);

      // Construir objeto de actualización incluyendo solo los campos proporcionados
      const dbUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.auditType !== undefined) dbUpdates.audit_type = updates.auditType;
      if (updates.scope !== undefined) dbUpdates.scope = updates.scope;
      if (updates.objectives !== undefined) dbUpdates.objectives = updates.objectives;
      if (updates.methodology !== undefined) dbUpdates.methodology = updates.methodology;
      if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
      if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
      if (updates.leadAuditorId !== undefined) dbUpdates.lead_auditor_id = updates.leadAuditorId;
      if (updates.auditTeam !== undefined) dbUpdates.audit_team = updates.auditTeam;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

      const { error: updateError } = await supabase
        .from('audit_plans')
        .update(dbUpdates)
        .eq('id', planId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Plan de auditoría actualizado');
      await loadAuditPlans();
    } catch (err) {
      handleError(err, 'actualizar plan de auditoría');
      throw err;
    }
  }, [loadAuditPlans, handleError]);

  /**
   * Elimina un plan de auditoría. Los hallazgos y acciones correctivas asociados
   * quedan huérfanos (o se eliminan en cascada dependiendo de la configuración de FK).
   *
   * @param planId - UUID del plan a eliminar
   */
  const deleteAuditPlan = useCallback(async (planId: string) => {
    try {
      logger.log('[AUDIT] 🗑️ Eliminando plan de auditoría:', planId);

      const { error: deleteError } = await supabase
        .from('audit_plans')
        .delete()
        .eq('id', planId);

      if (deleteError) throw deleteError;

      logger.log('[AUDIT] ✅ Plan de auditoría eliminado');
      await loadAuditPlans();
    } catch (err) {
      handleError(err, 'eliminar plan de auditoría');
      throw err;
    }
  }, [loadAuditPlans, handleError]);

  // =====================================================
  // FUNCIONES DE HALLAZGOS
  // =====================================================

  /**
   * Carga todos los hallazgos de auditoría con JOINs a:
   *   - `audit_plans`: contexto del plan al que pertenece el hallazgo
   *   - `iso_controls`: el control auditado (código, título, estado)
   *   - `users (auditor)`: quién identificó el hallazgo (FK explícita por alias)
   *   - `users (verifier)`: quién verificó la resolución (puede ser null)
   *
   * Los JOINs con FKs nominadas (`!audit_findings_auditor_id_fkey`) son necesarios
   * porque la tabla tiene dos FKs hacia `users` (auditor y verificador).
   */
  const loadFindings = useCallback(async () => {
    if (!user?.organization_id) return;

    try {
      logger.log('[AUDIT] 🔄 Cargando hallazgos...');

      const { data, error: queryError } = await supabase
        .from('audit_findings')
        .select(`
          *,
          audit_plan:audit_plans (id, name, audit_type, status),
          control:iso_controls (id, code, title, status),
          auditor:users!audit_findings_auditor_id_fkey (id, name, email, role, unit),
          verifier:users!audit_findings_verified_by_fkey (id, name, email, role, unit)
        `)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      const formattedFindings: AuditFinding[] = (data || []).map((finding: Record<string, unknown>) => ({
        id: finding.id,
        organizationId: finding.organization_id,
        auditPlanId: finding.audit_plan_id,
        controlId: finding.control_id,
        findingType: finding.finding_type,
        severity: finding.severity,
        title: finding.title,
        description: finding.description,
        evidenceReference: finding.evidence_reference || [],
        requirementsNotMet: finding.requirements_not_met || [],
        auditorId: finding.auditor_id,
        // El auditor siempre existe (NOT NULL en la BD) — acceso seguro sin optional chaining
        auditor: {
          id: finding.auditor.id,
          name: finding.auditor.name,
          email: finding.auditor.email,
          role: finding.auditor.role,
          unit: finding.auditor.unit,
          createdAt: '',
          isActive: true
        },
        identifiedDate: finding.identified_date,
        status: finding.status,
        resolutionNotes: finding.resolution_notes,
        resolvedAt: finding.resolved_at,
        verifiedBy: finding.verified_by,
        // El verificador puede ser null (hallazgos aún no verificados)
        verifier: finding.verifier ? {
          id: finding.verifier.id,
          name: finding.verifier.name,
          email: finding.verifier.email,
          role: finding.verifier.role,
          unit: finding.verifier.unit,
          createdAt: '',
          isActive: true
        } : undefined,
        verifiedAt: finding.verified_at,
        metadata: finding.metadata,
        createdAt: finding.created_at,
        updatedAt: finding.updated_at
      }));

      setFindings(formattedFindings);
      logger.log(`[AUDIT] ✅ Cargados ${formattedFindings.length} hallazgos`);
    } catch (err) {
      handleError(err, 'cargar hallazgos');
    }
  }, [user, handleError]);

  /**
   * Registra un nuevo hallazgo de auditoría.
   * El estado inicial por defecto es `'open'` si no se especifica.
   *
   * @param finding - Datos del hallazgo sin campos generados (id, timestamps, org)
   */
  const createFinding = useCallback(async (finding: Omit<AuditFinding, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[AUDIT] 📝 Creando hallazgo...', finding);

      const { data, error: insertError } = await supabase
        .from('audit_findings')
        .insert({
          organization_id: user.organization_id,
          audit_plan_id: finding.auditPlanId,
          control_id: finding.controlId,
          finding_type: finding.findingType,
          severity: finding.severity,
          title: finding.title,
          description: finding.description,
          evidence_reference: finding.evidenceReference || [],
          requirements_not_met: finding.requirementsNotMet || [],
          auditor_id: finding.auditorId,
          identified_date: finding.identifiedDate,
          status: finding.status || 'open',
          metadata: finding.metadata || {}
        })
        .select()
        .single();

      if (insertError) throw insertError;

      logger.log('[AUDIT] ✅ Hallazgo creado:', data);
      await loadFindings();
    } catch (err) {
      handleError(err, 'crear hallazgo');
      throw err;
    }
  }, [user, loadFindings, handleError]);

  /**
   * Actualiza los campos de un hallazgo existente.
   * Solo actualiza los campos presentes en `updates` (patrón defensivo).
   *
   * @param findingId - UUID del hallazgo
   * @param updates   - Campos a modificar en camelCase
   */
  const updateFinding = useCallback(async (findingId: string, updates: Partial<AuditFinding>) => {
    try {
      logger.log('[AUDIT] 🔄 Actualizando hallazgo:', findingId, updates);

      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.findingType !== undefined) dbUpdates.finding_type = updates.findingType;
      if (updates.severity !== undefined) dbUpdates.severity = updates.severity;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.evidenceReference !== undefined) dbUpdates.evidence_reference = updates.evidenceReference;
      if (updates.requirementsNotMet !== undefined) dbUpdates.requirements_not_met = updates.requirementsNotMet;
      if (updates.resolutionNotes !== undefined) dbUpdates.resolution_notes = updates.resolutionNotes;
      if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

      const { error: updateError } = await supabase
        .from('audit_findings')
        .update(dbUpdates)
        .eq('id', findingId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Hallazgo actualizado');
      await loadFindings();
    } catch (err) {
      handleError(err, 'actualizar hallazgo');
      throw err;
    }
  }, [loadFindings, handleError]);

  /**
   * Transiciona un hallazgo de `open` / `in_remediation` a `resolved`.
   * Establece `resolved_at` con la fecha actual (timestamp en BD).
   * La verificación posterior la hace un auditor diferente con `verifyFinding()`.
   *
   * @param findingId       - UUID del hallazgo a resolver
   * @param resolutionNotes - Descripción de cómo fue resuelto el hallazgo
   */
  const resolveFinding = useCallback(async (findingId: string, resolutionNotes: string) => {
    try {
      logger.log('[AUDIT] ✅ Resolviendo hallazgo:', findingId);

      const { error: updateError } = await supabase
        .from('audit_findings')
        .update({
          status: 'resolved',
          resolution_notes: resolutionNotes,
          resolved_at: new Date().toISOString()
        })
        .eq('id', findingId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Hallazgo resuelto');
      await loadFindings();
    } catch (err) {
      handleError(err, 'resolver hallazgo');
      throw err;
    }
  }, [loadFindings, handleError]);

  /**
   * Verifica la resolución de un hallazgo (transición `resolved` → `verified`).
   * Registra el ID del usuario actual como verificador y el timestamp de verificación.
   * Requiere que el usuario esté autenticado para registrar quién verificó.
   *
   * @param findingId - UUID del hallazgo a verificar
   */
  const verifyFinding = useCallback(async (findingId: string) => {
    if (!user?.id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[AUDIT] ✔️ Verificando hallazgo:', findingId);

      const { error: updateError } = await supabase
        .from('audit_findings')
        .update({
          status: 'verified',
          verified_by: user.id,
          verified_at: new Date().toISOString()
        })
        .eq('id', findingId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Hallazgo verificado');
      await loadFindings();
    } catch (err) {
      handleError(err, 'verificar hallazgo');
      throw err;
    }
  }, [user, loadFindings, handleError]);

  /**
   * Elimina un hallazgo de auditoría de la BD.
   *
   * @param findingId - UUID del hallazgo a eliminar
   */
  const deleteFinding = useCallback(async (findingId: string) => {
    try {
      logger.log('[AUDIT] 🗑️ Eliminando hallazgo:', findingId);

      const { error: deleteError } = await supabase
        .from('audit_findings')
        .delete()
        .eq('id', findingId);

      if (deleteError) throw deleteError;

      logger.log('[AUDIT] ✅ Hallazgo eliminado');
      await loadFindings();
    } catch (err) {
      handleError(err, 'eliminar hallazgo');
      throw err;
    }
  }, [loadFindings, handleError]);

  // =====================================================
  // FUNCIONES DE ACCIONES CORRECTIVAS
  // =====================================================

  /**
   * Carga todas las acciones correctivas con JOINs a:
   *   - `audit_findings`: contexto del hallazgo que originó la acción
   *   - `users (responsible)`: responsable de implementar la acción
   *   - `users (verifier)`: quien verificó la efectividad (puede ser null)
   *
   * Los alias de JOINs con FKs nominadas son necesarios por la misma razón que en hallazgos.
   */
  const loadCorrectiveActions = useCallback(async () => {
    if (!user?.organization_id) return;

    try {
      logger.log('[AUDIT] 🔄 Cargando acciones correctivas...');

      const { data, error: queryError } = await supabase
        .from('corrective_actions')
        .select(`
          *,
          finding:audit_findings (id, title, finding_type, severity, status),
          responsible_user:users!corrective_actions_responsible_user_id_fkey (id, name, email, role, unit),
          verifier:users!corrective_actions_verified_by_fkey (id, name, email, role, unit)
        `)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      const formattedActions: CorrectiveAction[] = (data || []).map((action: Record<string, unknown>) => ({
        id: action.id,
        organizationId: action.organization_id,
        findingId: action.finding_id,
        title: action.title,
        description: action.description,
        rootCauseAnalysis: action.root_cause_analysis,
        actionPlan: action.action_plan,
        responsibleUserId: action.responsible_user_id,
        responsibleUser: {
          id: action.responsible_user.id,
          name: action.responsible_user.name,
          email: action.responsible_user.email,
          role: action.responsible_user.role,
          unit: action.responsible_user.unit,
          createdAt: '',
          isActive: true
        },
        dueDate: action.due_date,
        implementedDate: action.implemented_date,
        status: action.status,
        verificationEvidence: action.verification_evidence,
        verifiedBy: action.verified_by,
        verifier: action.verifier ? {
          id: action.verifier.id,
          name: action.verifier.name,
          email: action.verifier.email,
          role: action.verifier.role,
          unit: action.verifier.unit,
          createdAt: '',
          isActive: true
        } : undefined,
        verifiedAt: action.verified_at,
        effectivenessReview: action.effectiveness_review,
        priority: action.priority,
        metadata: action.metadata,
        createdAt: action.created_at,
        updatedAt: action.updated_at
      }));

      setCorrectiveActions(formattedActions);
      logger.log(`[AUDIT] ✅ Cargadas ${formattedActions.length} acciones correctivas`);
    } catch (err) {
      handleError(err, 'cargar acciones correctivas');
    }
  }, [user, handleError]);

  /**
   * Crea una acción correctiva vinculada a un hallazgo.
   * Como efecto secundario, transiciona el hallazgo a `in_remediation` para
   * reflejar que ya existe un plan de corrección activo.
   *
   * @param action - Datos de la acción sin campos generados por PostgreSQL
   */
  const createCorrectiveAction = useCallback(async (action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[AUDIT] 📝 Creando acción correctiva...', action);

      const { data, error: insertError } = await supabase
        .from('corrective_actions')
        .insert({
          organization_id: user.organization_id,
          finding_id: action.findingId,
          title: action.title,
          description: action.description,
          root_cause_analysis: action.rootCauseAnalysis,
          action_plan: action.actionPlan,
          responsible_user_id: action.responsibleUserId,
          due_date: action.dueDate,
          status: action.status || 'pending',
          priority: action.priority || 'medium',
          metadata: action.metadata || {}
        })
        .select()
        .single();

      if (insertError) throw insertError;

      logger.log('[AUDIT] ✅ Acción correctiva creada:', data);

      // Transicionar el hallazgo a "in_remediation" para indicar que tiene plan activo
      await supabase
        .from('audit_findings')
        .update({ status: 'in_remediation' })
        .eq('id', action.findingId);

      // Recargar ambas entidades para mantener consistencia de estado
      await loadCorrectiveActions();
      await loadFindings();
    } catch (err) {
      handleError(err, 'crear acción correctiva');
      throw err;
    }
  }, [user, loadCorrectiveActions, loadFindings, handleError]);

  /**
   * Actualiza los campos de una acción correctiva existente.
   * Solo incluye en el UPDATE los campos explícitamente proporcionados.
   *
   * @param actionId - UUID de la acción a actualizar
   * @param updates  - Campos en camelCase (convertidos a snake_case para la BD)
   */
  const updateCorrectiveAction = useCallback(async (actionId: string, updates: Partial<CorrectiveAction>) => {
    try {
      logger.log('[AUDIT] 🔄 Actualizando acción correctiva:', actionId, updates);

      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.rootCauseAnalysis !== undefined) dbUpdates.root_cause_analysis = updates.rootCauseAnalysis;
      if (updates.actionPlan !== undefined) dbUpdates.action_plan = updates.actionPlan;
      if (updates.responsibleUserId !== undefined) dbUpdates.responsible_user_id = updates.responsibleUserId;
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
      if (updates.implementedDate !== undefined) dbUpdates.implemented_date = updates.implementedDate;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.verificationEvidence !== undefined) dbUpdates.verification_evidence = updates.verificationEvidence;
      if (updates.effectivenessReview !== undefined) dbUpdates.effectiveness_review = updates.effectivenessReview;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
      if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

      const { error: updateError } = await supabase
        .from('corrective_actions')
        .update(dbUpdates)
        .eq('id', actionId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Acción correctiva actualizada');
      await loadCorrectiveActions();
    } catch (err) {
      handleError(err, 'actualizar acción correctiva');
      throw err;
    }
  }, [loadCorrectiveActions, handleError]);

  /**
   * Marca una acción correctiva como completada con su evidencia de verificación.
   * Establece `implemented_date` con la fecha actual (solo la parte de fecha YYYY-MM-DD).
   *
   * @param actionId            - UUID de la acción
   * @param verificationEvidence - Descripción de la evidencia que demuestra la implementación
   */
  const completeCorrectiveAction = useCallback(async (actionId: string, verificationEvidence: string) => {
    try {
      logger.log('[AUDIT] ✅ Completando acción correctiva:', actionId);

      const { error: updateError } = await supabase
        .from('corrective_actions')
        .update({
          status: 'completed',
          // Solo la parte de fecha (YYYY-MM-DD) — la columna implemented_date es DATE en PostgreSQL
          implemented_date: new Date().toISOString().split('T')[0],
          verification_evidence: verificationEvidence
        })
        .eq('id', actionId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Acción correctiva completada');
      await loadCorrectiveActions();
    } catch (err) {
      handleError(err, 'completar acción correctiva');
      throw err;
    }
  }, [loadCorrectiveActions, handleError]);

  /**
   * Verifica la efectividad de una acción correctiva completada (→ `verified`).
   * Registra el usuario verificador y el review de efectividad.
   *
   * @param actionId          - UUID de la acción a verificar
   * @param effectivenessReview - Evaluación de si la acción resolvió la causa raíz
   */
  const verifyCorrectiveAction = useCallback(async (actionId: string, effectivenessReview: string) => {
    if (!user?.id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[AUDIT] ✔️ Verificando acción correctiva:', actionId);

      const { error: updateError } = await supabase
        .from('corrective_actions')
        .update({
          status: 'verified',
          verified_by: user.id,
          verified_at: new Date().toISOString(),
          effectiveness_review: effectivenessReview
        })
        .eq('id', actionId);

      if (updateError) throw updateError;

      logger.log('[AUDIT] ✅ Acción correctiva verificada');
      await loadCorrectiveActions();
    } catch (err) {
      handleError(err, 'verificar acción correctiva');
      throw err;
    }
  }, [user, loadCorrectiveActions, handleError]);

  /**
   * Elimina una acción correctiva de la BD.
   *
   * @param actionId - UUID de la acción a eliminar
   */
  const deleteCorrectiveAction = useCallback(async (actionId: string) => {
    try {
      logger.log('[AUDIT] 🗑️ Eliminando acción correctiva:', actionId);

      const { error: deleteError } = await supabase
        .from('corrective_actions')
        .delete()
        .eq('id', actionId);

      if (deleteError) throw deleteError;

      logger.log('[AUDIT] ✅ Acción correctiva eliminada');
      await loadCorrectiveActions();
    } catch (err) {
      handleError(err, 'eliminar acción correctiva');
      throw err;
    }
  }, [loadCorrectiveActions, handleError]);

  // =====================================================
  // FUNCIONES DE MÉTRICAS
  // =====================================================

  /**
   * Carga las métricas de auditoría usando la función RPC `get_audit_metrics`.
   * La RPC calcula agregaciones en PostgreSQL (más eficiente que traer todos los datos).
   *
   * Los desgloses por tipo, severidad, estado (hallazgos y acciones) se calculan
   * en el cliente usando `reduce()` sobre los arrays ya cargados en el estado.
   * Esto evita queries adicionales para información que ya está disponible localmente.
   *
   * Depende de `findings` y `correctiveActions` en el closure — estos se usan
   * para enriquecer el resultado de la RPC con los breakdowns detallados.
   */
  const loadAuditMetrics = useCallback(async () => {
    if (!user?.organization_id) return;

    try {
      logger.log('[AUDIT] 📊 Cargando métricas de auditoría...');

      const { data, error: queryError } = await supabase
        .rpc('get_audit_metrics', { org_id: user.organization_id });

      if (queryError) throw queryError;

      // Calcular desgloses localmente usando los datos ya cargados en el estado
      const findingsByType = findings.reduce((acc, f) => {
        acc[f.findingType] = (acc[f.findingType] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const findingsBySeverity = findings.reduce((acc, f) => {
        acc[f.severity] = (acc[f.severity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const findingsByStatus = findings.reduce((acc, f) => {
        acc[f.status] = (acc[f.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const actionsByStatus = correctiveActions.reduce((acc, a) => {
        acc[a.status] = (acc[a.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const metrics: AuditMetrics = {
        // Conteos principales desde la RPC (calculados en PostgreSQL)
        totalFindings: data?.total_findings || 0,
        openFindings: data?.open_findings || 0,
        criticalFindings: data?.critical_findings || 0,
        totalCorrectiveActions: data?.total_corrective_actions || 0,
        overdueActions: data?.overdue_actions || 0,
        activeAuditPlans: data?.active_audit_plans || 0,
        // Desgloses calculados localmente desde el estado React
        findingsByType: {
          non_conformity: findingsByType.non_conformity || 0,
          observation: findingsByType.observation || 0,
          opportunity: findingsByType.opportunity || 0
        },
        findingsBySeverity: {
          critical: findingsBySeverity.critical || 0,
          major: findingsBySeverity.major || 0,
          minor: findingsBySeverity.minor || 0,
          low: findingsBySeverity.low || 0
        },
        findingsByStatus: {
          open: findingsByStatus.open || 0,
          in_remediation: findingsByStatus.in_remediation || 0,
          resolved: findingsByStatus.resolved || 0,
          verified: findingsByStatus.verified || 0,
          accepted_risk: findingsByStatus.accepted_risk || 0
        },
        actionsByStatus: {
          pending: actionsByStatus.pending || 0,
          in_progress: actionsByStatus.in_progress || 0,
          completed: actionsByStatus.completed || 0,
          verified: actionsByStatus.verified || 0,
          rejected: actionsByStatus.rejected || 0
        }
      };

      setAuditMetrics(metrics);
      logger.log('[AUDIT] ✅ Métricas cargadas:', metrics);
    } catch (err) {
      handleError(err, 'cargar métricas de auditoría');
    }
  }, [user, findings, correctiveActions, handleError]);

  // =====================================================
  // FASE 3.5 — HISTORIAL DE REPORTES
  // =====================================================

  /**
   * Carga el historial de reportes generados de la organización.
   * Limitado a 100 reportes más recientes para evitar carga excesiva.
   */
  const loadSavedReports = useCallback(async () => {
    if (!user?.organization_id) return;
    try {
      const { data, error: err } = await supabase
        .from('audit_reports')
        .select('*')
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (err) throw err;
      const mapped: AuditReport[] = (data || []).map((r) => ({
        id: r.id,
        organizationId: r.organization_id,
        auditPlanId: r.audit_plan_id ?? undefined,
        name: r.name,
        description: r.description ?? undefined,
        reportType: r.report_type,
        format: r.format,
        status: r.status,
        generatedBy: r.generated_by ?? undefined,
        fileUrl: r.file_url ?? undefined,
        filePath: r.file_path ?? undefined,
        fileSize: r.file_size ?? undefined,
        templateId: r.template_id ?? undefined,
        findingsCount: r.findings_count ?? 0,
        actionsCount: r.actions_count ?? 0,
        errorMessage: r.error_message ?? undefined,
        includeSections: r.include_sections ?? {},
        filters: r.filters ?? undefined,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));
      setSavedReports(mapped);
      logger.log('[AUDIT] ✅ Reportes guardados cargados:', mapped.length);
    } catch (err) {
      handleError(err, 'cargar reportes guardados');
    }
  }, [user, handleError]);

  /**
   * Persiste un reporte generado (blob) en Supabase Storage y registra sus metadatos en BD.
   *
   * ## Flujo detallado
   * 1. Sube el blob al bucket `reports` con path `{orgId}/{timestamp}_{filename}`
   * 2. Si la subida falla (modo demo, bucket no configurado), continúa sin archivo físico
   * 3. Si la subida fue exitosa, genera una URL firmada con validez de 7 días
   * 4. Inserta el registro en `audit_reports` con la URL, path, tamaño y metadatos
   * 5. Actualiza el estado optimistamente sin recargar todos los reportes
   *
   * @param blob     - Contenido binario del reporte (PDF, Excel o CSV)
   * @param filename - Nombre del archivo (sin ruta, incluye extensión)
   * @param meta     - Metadatos del reporte (tipo, formato, secciones, filtros)
   * @returns El objeto `AuditReport` creado con todos los campos, o `null` si falla
   */
  const saveReport = useCallback(async (
    blob: Blob,
    filename: string,
    meta: {
      name: string;
      reportType: AuditReport['reportType'];
      format: AuditReport['format'];
      auditPlanId?: string;
      templateId?: string;
      filters?: AuditReport['filters'];
      includeSections: AuditReport['includeSections'];
      findingsCount?: number;
      actionsCount?: number;
    }
  ): Promise<AuditReport | null> => {
    if (!user?.organization_id) return null;
    try {
      const orgId = user.organization_id;
      // Path único usando timestamp — evita colisiones con reportes del mismo nombre
      const filePath = `${orgId}/${Date.now()}_${filename}`;
      const file = new File([blob], filename, { type: blob.type });

      // Intentar subir al Storage (falla silenciosamente en modo demo)
      const { data: storageData, error: storageErr } = await supabase.storage
        .from('reports')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (storageErr) {
        logger.warn('[AUDIT] ⚠️ No se pudo subir al Storage (modo demo?):', storageErr.message);
      }

      // Generar URL firmada solo si la subida fue exitosa
      let fileUrl: string | undefined;
      if (storageData) {
        const { data: signedData } = await supabase.storage
          .from('reports')
          .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 días de validez
        fileUrl = signedData?.signedUrl;
      }

      // Registrar metadatos del reporte en la BD independientemente del Storage
      const { data: row, error: dbErr } = await supabase
        .from('audit_reports')
        .insert({
          organization_id: orgId,
          name: meta.name,
          report_type: meta.reportType,
          format: meta.format,
          status: 'completed',
          file_url: fileUrl ?? null,
          file_path: storageData ? filePath : null, // null si Storage no disponible
          file_size: blob.size,
          audit_plan_id: meta.auditPlanId ?? null,
          template_id: meta.templateId ?? null,
          generated_by: user.id,
          filters: meta.filters ?? {},
          include_sections: meta.includeSections,
          findings_count: meta.findingsCount ?? 0,
          actions_count: meta.actionsCount ?? 0,
        })
        .select('*')
        .single();

      if (dbErr) throw dbErr;

      const newReport: AuditReport = {
        id: row.id,
        organizationId: row.organization_id,
        name: row.name,
        reportType: row.report_type,
        format: row.format,
        status: row.status,
        generatedBy: row.generated_by,
        fileUrl: row.file_url ?? undefined,
        filePath: row.file_path ?? undefined,
        fileSize: row.file_size ?? undefined,
        findingsCount: row.findings_count ?? 0,
        actionsCount: row.actions_count ?? 0,
        includeSections: row.include_sections,
        filters: row.filters,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      };

      // Actualización optimista: agregar al inicio de la lista sin reload completo
      setSavedReports(prev => [newReport, ...prev]);
      logger.log('[AUDIT] ✅ Reporte guardado:', newReport.id);
      return newReport;
    } catch (err) {
      handleError(err, 'guardar reporte');
      return null;
    }
  }, [user, handleError]);

  /**
   * Elimina un reporte del historial.
   * Si el reporte tiene archivo en Storage (`filePath`), lo elimina primero.
   * Actualiza el estado localmente sin recargar toda la lista.
   *
   * @param reportId - UUID del reporte a eliminar
   */
  const deleteReport = useCallback(async (reportId: string) => {
    if (!user?.organization_id) return;
    try {
      // Eliminar archivo físico del Storage si existe
      const report = savedReports.find(r => r.id === reportId);
      if (report?.filePath) {
        await supabase.storage.from('reports').remove([report.filePath]);
      }
      const { error: err } = await supabase
        .from('audit_reports')
        .delete()
        .eq('id', reportId);
      if (err) throw err;
      setSavedReports(prev => prev.filter(r => r.id !== reportId));
      logger.log('[AUDIT] ✅ Reporte eliminado:', reportId);
    } catch (err) {
      handleError(err, 'eliminar reporte');
    }
  }, [user, savedReports, handleError]);

  /**
   * Descarga un reporte desde Supabase Storage al navegador del usuario.
   * Genera una URL firmada fresca (7 días) para evitar URLs expiradas,
   * descarga el contenido como blob y dispara la descarga del navegador.
   *
   * @param report - Objeto `AuditReport` con `filePath` para generar la URL firmada
   */
  const downloadReport = useCallback(async (report: AuditReport) => {
    if (!report.filePath) {
      logger.warn('[AUDIT] ⚠️ El reporte no tiene archivo en Storage');
      return;
    }
    try {
      // Generar URL firmada fresca — la URL guardada en fileUrl puede haber expirado
      const { data, error: err } = await supabase.storage
        .from('reports')
        .createSignedUrl(report.filePath, 60 * 60 * 24 * 7);
      if (err || !data) throw err ?? new Error('No se pudo generar URL de descarga');

      // Descargar como blob y disparar descarga nativa del navegador
      const resp = await fetch(data.signedUrl);
      const blob = await resp.blob();
      const ext = report.format === 'pdf' ? 'pdf' : report.format === 'excel' ? 'xlsx' : 'csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Liberar memoria — el object URL no se necesita después de disparar la descarga
      URL.revokeObjectURL(url);
      logger.log('[AUDIT] ✅ Reporte descargado desde Storage:', report.id);
    } catch (err) {
      handleError(err, 'descargar reporte');
    }
  }, [handleError]);

  // =====================================================
  // FASE 3.5 — PLANTILLAS DE REPORTE
  // =====================================================

  /**
   * Carga las plantillas de reporte de la organización.
   * Las plantillas definen colores de marca, logo, textos de cabecera/pie
   * y las secciones predeterminadas a incluir en los reportes.
   */
  const loadReportTemplates = useCallback(async () => {
    if (!user?.organization_id) return;
    try {
      const { data, error: err } = await supabase
        .from('report_templates')
        .select('*')
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      const mapped: ReportTemplate[] = (data || []).map((t) => ({
        id: t.id,
        organizationId: t.organization_id,
        name: t.name,
        description: t.description ?? undefined,
        primaryColor: t.primary_color,
        secondaryColor: t.secondary_color,
        logoUrl: t.logo_url ?? undefined,
        logoPath: t.logo_path ?? undefined,
        headerText: t.header_text ?? undefined,
        footerText: t.footer_text ?? undefined,
        reportType: t.report_type,
        includeSections: t.include_sections ?? {},
        isDefault: t.is_default,
        createdBy: t.created_by ?? undefined,
        createdAt: t.created_at,
        updatedAt: t.updated_at,
      }));
      setReportTemplates(mapped);
    } catch (err) {
      handleError(err, 'cargar plantillas de reporte');
    }
  }, [user, handleError]);

  /**
   * Crea una nueva plantilla de reporte.
   * Si `isDefault` es true, primero quita el flag de default de todas las existentes
   * para garantizar que solo haya una plantilla por defecto por organización.
   *
   * @param template - Datos de la plantilla sin campos generados por PostgreSQL
   * @returns La plantilla creada, o `null` si falla
   */
  const createReportTemplate = useCallback(async (
    template: Omit<ReportTemplate, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>
  ): Promise<ReportTemplate | null> => {
    if (!user?.organization_id) return null;
    try {
      // Garantizar unicidad del default antes de crear la nueva plantilla
      if (template.isDefault) {
        await supabase
          .from('report_templates')
          .update({ is_default: false })
          .eq('organization_id', user.organization_id);
      }
      const { data, error: err } = await supabase
        .from('report_templates')
        .insert({
          organization_id: user.organization_id,
          name: template.name,
          description: template.description ?? null,
          primary_color: template.primaryColor,
          secondary_color: template.secondaryColor,
          logo_url: template.logoUrl ?? null,
          logo_path: template.logoPath ?? null,
          header_text: template.headerText ?? null,
          footer_text: template.footerText ?? null,
          report_type: template.reportType,
          include_sections: template.includeSections,
          is_default: template.isDefault,
          created_by: user.id,
        })
        .select('*')
        .single();
      if (err) throw err;
      const newTemplate: ReportTemplate = {
        id: data.id,
        organizationId: data.organization_id,
        name: data.name,
        description: data.description ?? undefined,
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        logoUrl: data.logo_url ?? undefined,
        logoPath: data.logo_path ?? undefined,
        headerText: data.header_text ?? undefined,
        footerText: data.footer_text ?? undefined,
        reportType: data.report_type,
        includeSections: data.include_sections,
        isDefault: data.is_default,
        createdBy: data.created_by ?? undefined,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
      // Actualización optimista — no recargar toda la lista
      setReportTemplates(prev => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err) {
      handleError(err, 'crear plantilla de reporte');
      return null;
    }
  }, [user, handleError]);

  /**
   * Actualiza una plantilla existente.
   * Si se está marcando como default, quita el flag de las demás primero
   * (usando `.neq('id', templateId)` para excluir la plantilla actual).
   *
   * @param templateId - UUID de la plantilla
   * @param updates    - Campos a actualizar en camelCase
   */
  const updateReportTemplate = useCallback(async (
    templateId: string,
    updates: Partial<ReportTemplate>
  ) => {
    if (!user?.organization_id) return;
    try {
      // Quitar default de otras plantillas antes de activar el default en esta
      if (updates.isDefault) {
        await supabase
          .from('report_templates')
          .update({ is_default: false })
          .eq('organization_id', user.organization_id)
          .neq('id', templateId); // Excluir la plantilla actual de la limpieza
      }
      const dbUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.primaryColor !== undefined) dbUpdates.primary_color = updates.primaryColor;
      if (updates.secondaryColor !== undefined) dbUpdates.secondary_color = updates.secondaryColor;
      if (updates.logoUrl !== undefined) dbUpdates.logo_url = updates.logoUrl;
      if (updates.logoPath !== undefined) dbUpdates.logo_path = updates.logoPath;
      if (updates.headerText !== undefined) dbUpdates.header_text = updates.headerText;
      if (updates.footerText !== undefined) dbUpdates.footer_text = updates.footerText;
      if (updates.reportType !== undefined) dbUpdates.report_type = updates.reportType;
      if (updates.includeSections !== undefined) dbUpdates.include_sections = updates.includeSections;
      if (updates.isDefault !== undefined) dbUpdates.is_default = updates.isDefault;

      const { error: err } = await supabase
        .from('report_templates')
        .update(dbUpdates)
        .eq('id', templateId);
      if (err) throw err;
      // Actualización optimista del estado local
      setReportTemplates(prev => prev.map(t =>
        t.id === templateId ? { ...t, ...updates } : t
      ));
    } catch (err) {
      handleError(err, 'actualizar plantilla de reporte');
    }
  }, [user, handleError]);

  /**
   * Elimina una plantilla y su logo del Storage si existe.
   *
   * @param templateId - UUID de la plantilla a eliminar
   */
  const deleteReportTemplate = useCallback(async (templateId: string) => {
    if (!user?.organization_id) return;
    try {
      // Eliminar logo del Storage si existe antes de eliminar el registro
      const template = reportTemplates.find(t => t.id === templateId);
      if (template?.logoPath) {
        await supabase.storage.from('reports').remove([template.logoPath]);
      }
      const { error: err } = await supabase
        .from('report_templates')
        .delete()
        .eq('id', templateId);
      if (err) throw err;
      setReportTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (err) {
      handleError(err, 'eliminar plantilla de reporte');
    }
  }, [user, reportTemplates, handleError]);

  /**
   * Sube un logo al bucket `reports` de Supabase Storage y actualiza la plantilla.
   * Usa `upsert: true` para sobreescribir el logo anterior si existe.
   * Retorna la URL pública del logo para previsualización inmediata en UI.
   *
   * @param templateId - UUID de la plantilla que recibirá el logo
   * @param file       - Archivo de imagen (PNG, JPG, SVG recomendados)
   * @returns URL pública del logo en Storage, o `null` si falla
   */
  const uploadTemplateLogo = useCallback(async (
    templateId: string,
    file: File
  ): Promise<string | null> => {
    if (!user?.organization_id) return null;
    try {
      // Path con timestamp para evitar caché del navegador al actualizar el logo
      const path = `${user.organization_id}/logos/${templateId}_${Date.now()}_${file.name}`;
      const { data, error: err } = await supabase.storage
        .from('reports')
        .upload(path, file, { cacheControl: '3600', upsert: true });
      if (err) throw err;
      const { data: { publicUrl } } = supabase.storage.from('reports').getPublicUrl(data.path);
      await updateReportTemplate(templateId, { logoUrl: publicUrl, logoPath: path });
      return publicUrl;
    } catch (err) {
      handleError(err, 'subir logo de plantilla');
      return null;
    }
  }, [user, updateReportTemplate, handleError]);

  // =====================================================
  // FASE 3.5 — PROGRAMACIÓN DE REPORTES
  // =====================================================

  /**
   * Carga las programaciones de reporte periódico de la organización.
   * Las programaciones se ejecutan externamente (cron job o Supabase Edge Function)
   * y el frontend solo gestiona la configuración, no la ejecución.
   */
  const loadReportSchedules = useCallback(async () => {
    if (!user?.organization_id) return;
    try {
      const { data, error: err } = await supabase
        .from('report_schedules')
        .select('*')
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      const mapped: ReportSchedule[] = (data || []).map((s) => ({
        id: s.id,
        organizationId: s.organization_id,
        name: s.name,
        description: s.description ?? undefined,
        templateId: s.template_id ?? undefined,
        reportType: s.report_type,
        format: s.format,
        frequency: s.frequency,
        dayOfWeek: s.day_of_week ?? undefined,
        dayOfMonth: s.day_of_month ?? undefined,
        sendTime: s.send_time,
        recipients: s.recipients ?? [],
        subjectTemplate: s.subject_template,
        bodyTemplate: s.body_template,
        filters: s.filters,
        includeSections: s.include_sections,
        isActive: s.is_active,
        nextRunAt: s.next_run_at ?? undefined,
        lastRunAt: s.last_run_at ?? undefined,
        lastReportId: s.last_report_id ?? undefined,
        createdBy: s.created_by ?? undefined,
        createdAt: s.created_at,
        updatedAt: s.updated_at,
      }));
      setReportSchedules(mapped);
    } catch (err) {
      handleError(err, 'cargar programaciones de reporte');
    }
  }, [user, handleError]);

  /**
   * Crea una nueva programación de reporte periódico.
   * Actualiza el estado optimistamente sin recargar toda la lista.
   *
   * @param schedule - Configuración de la programación sin campos generados por PostgreSQL
   */
  const createReportSchedule = useCallback(async (
    schedule: Omit<ReportSchedule, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>
  ) => {
    if (!user?.organization_id) return;
    try {
      const { data, error: err } = await supabase
        .from('report_schedules')
        .insert({
          organization_id: user.organization_id,
          name: schedule.name,
          description: schedule.description ?? null,
          template_id: schedule.templateId ?? null,
          report_type: schedule.reportType,
          format: schedule.format,
          frequency: schedule.frequency,
          day_of_week: schedule.dayOfWeek ?? null,
          day_of_month: schedule.dayOfMonth ?? null,
          send_time: schedule.sendTime,
          recipients: schedule.recipients,
          subject_template: schedule.subjectTemplate,
          body_template: schedule.bodyTemplate,
          filters: schedule.filters ?? {},
          include_sections: schedule.includeSections ?? {},
          is_active: schedule.isActive,
          created_by: user.id,
        })
        .select('*')
        .single();
      if (err) throw err;
      const newSchedule: ReportSchedule = {
        id: data.id,
        organizationId: data.organization_id,
        name: data.name,
        description: data.description ?? undefined,
        templateId: data.template_id ?? undefined,
        reportType: data.report_type,
        format: data.format,
        frequency: data.frequency,
        dayOfWeek: data.day_of_week ?? undefined,
        dayOfMonth: data.day_of_month ?? undefined,
        sendTime: data.send_time,
        recipients: data.recipients ?? [],
        subjectTemplate: data.subject_template,
        bodyTemplate: data.body_template,
        isActive: data.is_active,
        nextRunAt: data.next_run_at ?? undefined,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
      setReportSchedules(prev => [newSchedule, ...prev]);
    } catch (err) {
      handleError(err, 'crear programación de reporte');
    }
  }, [user, handleError]);

  /**
   * Actualiza la configuración de una programación existente.
   * Solo actualiza los campos explícitamente proporcionados.
   *
   * @param scheduleId - UUID de la programación
   * @param updates    - Campos a actualizar en camelCase
   */
  const updateReportSchedule = useCallback(async (
    scheduleId: string,
    updates: Partial<ReportSchedule>
  ) => {
    if (!user?.organization_id) return;
    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.templateId !== undefined) dbUpdates.template_id = updates.templateId;
      if (updates.reportType !== undefined) dbUpdates.report_type = updates.reportType;
      if (updates.format !== undefined) dbUpdates.format = updates.format;
      if (updates.frequency !== undefined) dbUpdates.frequency = updates.frequency;
      if (updates.dayOfWeek !== undefined) dbUpdates.day_of_week = updates.dayOfWeek;
      if (updates.dayOfMonth !== undefined) dbUpdates.day_of_month = updates.dayOfMonth;
      if (updates.sendTime !== undefined) dbUpdates.send_time = updates.sendTime;
      if (updates.recipients !== undefined) dbUpdates.recipients = updates.recipients;
      if (updates.subjectTemplate !== undefined) dbUpdates.subject_template = updates.subjectTemplate;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;

      const { error: err } = await supabase
        .from('report_schedules')
        .update(dbUpdates)
        .eq('id', scheduleId);
      if (err) throw err;
      // Actualización optimista del estado local
      setReportSchedules(prev => prev.map(s =>
        s.id === scheduleId ? { ...s, ...updates } : s
      ));
    } catch (err) {
      handleError(err, 'actualizar programación de reporte');
    }
  }, [user, handleError]);

  /**
   * Elimina una programación de reporte.
   * Actualiza el estado localmente sin recargar toda la lista.
   *
   * @param scheduleId - UUID de la programación a eliminar
   */
  const deleteReportSchedule = useCallback(async (scheduleId: string) => {
    if (!user?.organization_id) return;
    try {
      const { error: err } = await supabase
        .from('report_schedules')
        .delete()
        .eq('id', scheduleId);
      if (err) throw err;
      setReportSchedules(prev => prev.filter(s => s.id !== scheduleId));
    } catch (err) {
      handleError(err, 'eliminar programación de reporte');
    }
  }, [user, handleError]);

  /**
   * Activa o desactiva una programación sin modificar otros campos.
   * Shortcut sobre `updateReportSchedule` para la acción más común desde la UI.
   *
   * @param scheduleId - UUID de la programación
   * @param isActive   - Nuevo estado de activación
   */
  const toggleScheduleActive = useCallback(async (scheduleId: string, isActive: boolean) => {
    await updateReportSchedule(scheduleId, { isActive });
  }, [updateReportSchedule]);

  // =====================================================
  // REFRESH DATA
  // =====================================================

  /**
   * Recarga todos los módulos del contexto de auditoría.
   * Carga en paralelo: planes, hallazgos, acciones, reportes, plantillas, programaciones.
   * Las métricas se cargan secuencialmente DESPUÉS porque dependen de `findings` y
   * `correctiveActions` ya actualizados en el estado para los breakdowns locales.
   *
   * Gestiona `isLoading` a nivel del contexto (no por función individual) para
   * mostrar un spinner unificado durante la carga inicial completa.
   */
  const refreshData = useCallback(async () => {
    if (!user?.organization_id) return;

    setIsLoading(true);
    try {
      // Cargar todos los datos en paralelo para minimizar tiempo de espera
      await Promise.all([
        loadAuditPlans(),
        loadFindings(),
        loadCorrectiveActions(),
        loadSavedReports(),
        loadReportTemplates(),
        loadReportSchedules(),
      ]);
      // Las métricas van después porque usan findings y correctiveActions del estado
      await loadAuditMetrics();
    } catch (err) {
      logger.error('[AUDIT] Error refrescando datos:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, loadAuditPlans, loadFindings, loadCorrectiveActions, loadAuditMetrics, loadSavedReports, loadReportTemplates, loadReportSchedules]);

  // =====================================================
  // EFECTOS
  // =====================================================

  /**
   * Dispara la carga inicial cuando el usuario está autenticado y sincronizado con BD.
   * La condición `dbUserReady` garantiza que `user.organization_id` proviene de
   * `public.users` (no solo del JWT de Auth), evitando cargar datos con un org_id
   * potencialmente desactualizado o vacío.
   */
  useEffect(() => {
    if (user?.organization_id && dbUserReady) {
      refreshData();
    }
  }, [user?.organization_id, dbUserReady]);

  // =====================================================
  // VALOR DEL CONTEXTO
  // =====================================================

  const value: AuditContextType = {
    // Estados
    auditPlans,
    findings,
    correctiveActions,
    auditMetrics,
    savedReports,
    reportTemplates,
    reportSchedules,
    isLoading,
    error,

    // Utilidades
    clearError,
    refreshData,

    // Planes de auditoría
    loadAuditPlans,
    createAuditPlan,
    updateAuditPlan,
    deleteAuditPlan,

    // Hallazgos
    loadFindings,
    createFinding,
    updateFinding,
    deleteFinding,
    resolveFinding,
    verifyFinding,

    // Acciones correctivas
    loadCorrectiveActions,
    createCorrectiveAction,
    updateCorrectiveAction,
    deleteCorrectiveAction,
    completeCorrectiveAction,
    verifyCorrectiveAction,

    // Métricas
    loadAuditMetrics,

    // Fase 3.5 — Historial
    loadSavedReports,
    saveReport,
    deleteReport,
    downloadReport,

    // Fase 3.5 — Plantillas
    loadReportTemplates,
    createReportTemplate,
    updateReportTemplate,
    deleteReportTemplate,
    uploadTemplateLogo,

    // Fase 3.5 — Programación
    loadReportSchedules,
    createReportSchedule,
    updateReportSchedule,
    deleteReportSchedule,
    toggleScheduleActive
  };

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
}

// =====================================================
// HOOK
// =====================================================

/**
 * Hook para consumir el contexto de auditoría en componentes funcionales.
 * Lanza un error descriptivo si se usa fuera de `AuditProvider`.
 *
 * @returns El valor completo del `AuditContextType`
 * @throws Error si el hook se usa fuera del árbol del provider
 *
 * @example
 * const { findings, createFinding, auditMetrics } = useAudit();
 */
export function useAudit() {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error('useAudit debe ser usado dentro de un AuditProvider');
  }
  return context;
}
